import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, ShieldCheck, Store, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDarkMode } from '@/hooks/useDarkMode';

const SEEN_KEY = 'mementa_purchase_explainer_seen';

export function shouldShowPurchaseExplainer() {
  try {
    return !localStorage.getItem(SEEN_KEY);
  } catch {
    return false;
  }
}

// One-time popup explaining how buying works: Mementa curates, the purchase
// itself happens on the merchant's site (no Mementa account needed to buy).
export default function PurchaseExplainer({ onDismiss }) {
  const darkMode = useDarkMode();
  const [closing, setClosing] = useState(false);
  const isDark = darkMode.isDark;

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
    setClosing(true);
    setTimeout(onDismiss, 200);
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center p-4"
      style={{
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(3px)',
        zIndex: 100000,
        opacity: closing ? 0 : 1,
        transition: 'opacity 0.2s ease'
      }}
      onClick={dismiss}
      data-testid="purchase-explainer"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        style={{ background: isDark ? '#2D4354' : 'white' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 relative" style={{ background: 'linear-gradient(135deg, #9E6752 0%, #534145 100%)' }}>
          <button onClick={dismiss} className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors" data-testid="purchase-explainer-close">
            <X className="w-5 h-5 text-white" />
          </button>
          <Store className="w-8 h-8 mb-3" style={{ color: '#FED7A5' }} />
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            How buying works
          </h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex gap-3">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: '#FED7A5', color: '#534145' }}>1</span>
            <p className="text-sm leading-relaxed" style={{ color: isDark ? '#FFFAF5' : '#534145' }}>
              We curate the gifts, every product links to a trusted merchant's own site.
            </p>
          </div>
          <div className="flex gap-3">
            <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: '#FED7A5', color: '#534145' }}>2</span>
            <p className="text-sm leading-relaxed" style={{ color: isDark ? '#FFFAF5' : '#534145' }}>
              Tap <span className="font-semibold inline-flex items-center gap-1">View on Merchant <ExternalLink className="w-3 h-3" /></span> and complete your purchase there, payment, shipping, and returns are all handled by the merchant.
            </p>
          </div>
          <div className="flex gap-3">
            <ShieldCheck className="w-6 h-6 flex-shrink-0" style={{ color: '#9E6752' }} />
            <p className="text-sm leading-relaxed" style={{ color: isDark ? '#A8A9A6' : '#73766A' }}>
              You never need a Mementa account to buy. Each merchant handles its own checkout, you may need an account with that merchant, or you can often check out as a guest.
            </p>
          </div>
        </div>
        <div className="px-6 pb-6">
          <Button onClick={dismiss} className="w-full" style={{ background: '#9E6752', color: 'white', borderRadius: '30px' }} data-testid="purchase-explainer-got-it">
            Got it
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
